import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import campaignApiService from "../../services/campaignApiService";

export const campaignKeys = {
  all: ["campaigns"],
  lists: () => [...campaignKeys.all, "list"],
  detail: (slug) => [...campaignKeys.all, "detail", slug],
  products: (slug) => [...campaignKeys.all, "products", slug],
  productSearch: (query) => [
    "campaign-product-search",
    query,
  ],
};

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return data;
  return data?.results || [];
};

export const useCampaignList = () => {
  return useQuery({
    queryKey: campaignKeys.lists(),
    queryFn: async () => {
      const data = await campaignApiService.getCampaigns();
      return normalizeListResponse(data);
    },
  });
};

export const useCampaign = (slug) => {
  return useQuery({
    queryKey: campaignKeys.detail(slug),
    queryFn: () => campaignApiService.getCampaign(slug),
    enabled: Boolean(slug),
  });
};

export const useExportCampaignProducts = () => {
  return useMutation({
    mutationFn: campaignApiService.getProductsExportData,
  });
};

export const useProductSearch = (query = "") => {
  const normalizedQuery = String(query).trim();

  return useQuery({
    queryKey: campaignKeys.productSearch(normalizedQuery),

    queryFn: async () => {
      const data =
        await campaignApiService.searchProducts(
          normalizedQuery
        );

      if (Array.isArray(data)) {
        return data;
      }

      return data?.results || [];
    },

    enabled: normalizedQuery.length >= 2,

    staleTime: 30 * 1000,
  });
};


export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: campaignApiService.createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.lists() });
    },
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: campaignApiService.updateCampaign,
    onSuccess: (campaign, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.lists() });
      if (variables?.slug) {
        queryClient.invalidateQueries({
          queryKey: campaignKeys.detail(variables.slug),
        });
      }
    },
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: campaignApiService.deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.lists() });
    },
  });
};

export const useAddCampaignProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: campaignApiService.addProduct,
    onSuccess: (data, variables) => {
      if (variables?.slug) {
        queryClient.invalidateQueries({
          queryKey: campaignKeys.detail(variables.slug),
        });
        queryClient.invalidateQueries({
          queryKey: campaignKeys.products(variables.slug),
        });
      }
    },
  });
};

export const useRemoveCampaignProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: campaignApiService.removeProduct,
    onSuccess: (data, variables) => {
      if (variables?.slug) {
        queryClient.invalidateQueries({
          queryKey: campaignKeys.detail(variables.slug),
        });
        queryClient.invalidateQueries({
          queryKey: campaignKeys.products(variables.slug),
        });
      }
    },
  });
};

export const useImportCampaignProducts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: campaignApiService.importProducts,
    onSuccess: (data, variables) => {
      if (variables?.slug) {
        queryClient.invalidateQueries({
          queryKey: campaignKeys.detail(variables.slug),
        });
        queryClient.invalidateQueries({
          queryKey: campaignKeys.products(variables.slug),
        });
      }
    },
  });
};
